import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Users, Search, FileText, Shield, Clock, MessageCircle, Camera, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SEO, { SEOConfig } from '../components/SEO';

export default function ForTenants() {
  const benefits = [
    {
      icon: FileText,
      title: "Reusable Tenant Profile",
      description: "Apply for multiple properties without repeating uploads. Your ID, references, and documents are stored securely."
    },
    {
      icon: Shield,
      title: "Digital Agreements",
      description: "E-sign your tenancy agreement from anywhere. Clear timelines and status updates throughout."
    },
    {
      icon: MessageCircle,
      title: "Simple Repair Reporting",
      description: "Report issues in-app with photos. Track progress and communicate directly with landlords."
    },
    {
      icon: Clock,
      title: "Tenant-Led Inspections",
      description: "Complete inspections at your convenience within flexible windows. Less intrusion, more control."
    },
    {
      icon: Camera,
      title: "Evidence Storage",
      description: "All records, communications, and evidence stored in-app. Never lose important documents again."
    },
    {
      icon: Search,
      title: "Smart Property Search",
      description: "Find your perfect home with advanced filters, 3D tours, and transparent pricing."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      <SEO {...SEOConfig.forTenants} />
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/landlords" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Landlords
            </Link>
            <Link to="/tenants" className="text-blue-600 font-semibold">
              For Tenants
            </Link>
            <Link to="/properties" className="text-slate-600 hover:text-primary transition-colors font-medium">
              Browse Properties
            </Link>
            <Link to="/login">
              <Button>Sign In</Button>
            </Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/landlords" className="text-sm text-slate-700 font-medium">Landlords</Link>
            <Link to="/login"><Button size="sm">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4" />
            For Tenants
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-extrabold leading-tight mb-6">
            Find your home,
            <span className="text-blue-600 block">without the hassle.</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Apply for properties faster with a reusable tenant profile. Digital agreements, clear timelines, and all your records in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register?role=tenant">
              <Button size="lg" className="w-full sm:w-auto">
                Create Tenant Profile
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/properties">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                <Search className="w-5 h-5 mr-2" />
                Browse Properties
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Create Your Profile', description: 'Upload your ID, employment details, and references once. Use it for any application.' },
              { step: '2', title: 'Find & Apply', description: 'Browse properties and apply instantly with your saved profile. No repeated paperwork.' },
              { step: '3', title: 'Move In', description: 'Sign digitally, complete checks, and get your keys. Everything tracked in one place.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">The Tenant Promise</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            We&apos;ve designed LetWise to make renting simpler, more transparent, and less stressful.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="p-3 bg-blue-100 rounded-lg w-fit mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="p-12 text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <h2 className="text-3xl font-display font-bold mb-4">Ready to find your new home?</h2>
            <p className="text-xl mb-8 opacity-90">Create your profile once, apply anywhere.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=tenant">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100">
                  Create Tenant Profile
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/properties">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Browse Properties
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-400">&copy; 2025 LetWise. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
